import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Refine } from '@refinedev/core';
// import { RefineKbar, RefineKbarProvider } from '@refinedev/kbar';
// import routerProvider from '@refinedev/react-router-v6'; // Disabled - conflicts with custom route guards

import { AuthProvider, refineAuthProvider, useAuth } from '@/providers/auth-provider';
import { ThemeProvider } from '@/providers/theme-provider';
import { AdminLayout } from '@/components/layout/admin-layout';
import { PermissionGuard } from '@/components/auth/permission-guard';

// Lazy-loaded page components for code splitting (reduces initial bundle by ~60%)
const LoginPage = lazy(() => import('@/pages/auth/login').then(m => ({ default: m.LoginPage })));
const DashboardPage = lazy(() => import('@/pages/dashboard/dashboard').then(m => ({ default: m.DashboardPage })));
const PartnersListPage = lazy(() => import('@/pages/partners/partners-list').then(m => ({ default: m.PartnersListPage })));
const PartnerCreatePage = lazy(() => import('@/pages/partners/partner-create').then(m => ({ default: m.PartnerCreatePage })));
const PartnerDetailPage = lazy(() => import('@/pages/partners/partner-detail').then(m => ({ default: m.PartnerDetailPage })));
const BookingMonitorPage = lazy(() => import('@/pages/bookings/booking-monitor').then(m => ({ default: m.BookingMonitorPage })));
const AdminUsersListPage = lazy(() => import('@/pages/users/admin-users-list').then(m => ({ default: m.AdminUsersListPage })));
const StaffListPage = lazy(() => import('@/pages/staff/staff-list').then(m => ({ default: m.StaffListPage })));
const CustomerListPage = lazy(() => import('@/pages/customers/customer-list').then(m => ({ default: m.CustomerListPage })));
const AuditLogViewer = lazy(() => import('@/components/audit/audit-log-viewer').then(m => ({ default: m.AuditLogViewer })));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="text-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3"></div>
      <p className="text-gray-500 text-sm">Loading...</p>
    </div>
  </div>
);

import './App.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: (failureCount, error: any) => {
        if (error?.status === 404) return false;
        return failureCount < 3;
      },
    },
  },
});

// Theme configuration is now handled by ThemeProvider

// SECURITY: Domain-scoped auth token key (VULN-004 fix)
const ADMIN_AUTH_TOKEN_KEY = 'admin-auth-token';

/**
 * Minimal data provider stub for Refine framework.
 *
 * NOTE: This is a stub required by Refine's core component.
 * All actual data fetching is done via React Query hooks that call Supabase directly.
 * Do NOT use Refine's useList/useOne/etc hooks - use the custom hooks in /hooks/ instead.
 */
const dataProvider = {
  getList: async () => ({ data: [], total: 0 }),
  getOne: async ({ id }: { id: string }) => ({ data: { id } }),
  create: async ({ variables }: { variables: Record<string, unknown> }) => ({ data: { id: '1', ...variables } }),
  update: async ({ id, variables }: { id: string; variables: Record<string, unknown> }) => ({ data: { id, ...variables } }),
  deleteOne: async () => ({ data: {} }),
  getApiUrl: () => import.meta.env.VITE_SUPABASE_URL || 'https://supabase.co',
};

// Public Route wrapper - redirects authenticated users to dashboard
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return null;
  }

  return <>{children}</>;
};

// Protected Route wrapper with authentication check
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <AdminLayout>
      {children}
    </AdminLayout>
  );
};

// Main App component
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          {/* <RefineKbarProvider> */}
            <AuthProvider>
              <Refine
                  // routerProvider={routerProvider} // Disabled - conflicts with custom route guards
                  dataProvider={dataProvider}
                  authProvider={refineAuthProvider}
                  resources={[
                    {
                      name: 'dashboard',
                      list: '/dashboard',
                      meta: {
                        label: 'Dashboard',
                        icon: '📊',
                      },
                    },
                    {
                      name: 'partners',
                      list: '/partners',
                      show: '/partners/:id',
                      create: '/partners/new',
                      edit: '/partners/:id/edit',
                      meta: {
                        label: 'Partners',
                        icon: '🏪',
                      },
                    },
                    {
                      name: 'bookings',
                      list: '/bookings',
                      show: '/bookings/:id',
                      meta: {
                        label: 'Bookings',
                        icon: '📅',
                      },
                    },
                    {
                      name: 'users',
                      list: '/users',
                      show: '/users/:id',
                      create: '/users/new',
                      edit: '/users/:id/edit',
                      meta: {
                        label: 'Users',
                        icon: '👥',
                      },
                    },
                    {
                      name: 'staff',
                      list: '/staff',
                      show: '/staff/:id',
                      create: '/staff/new',
                      edit: '/staff/:id/edit',
                      meta: {
                        label: 'Staff',
                        icon: '👔',
                      },
                    },
                    {
                      name: 'analytics',
                      list: '/analytics',
                      meta: {
                        label: 'Analytics',
                        icon: '📈',
                      },
                    },
                    {
                      name: 'support',
                      list: '/support/tickets',
                      show: '/support/tickets/:id',
                      create: '/support/tickets/new',
                      meta: {
                        label: 'Support',
                        icon: '🎧',
                      },
                    },
                    {
                      name: 'settings',
                      list: '/settings',
                      meta: {
                        label: 'Settings',
                        icon: '⚙️',
                      },
                    },
                  ]}
                  options={{
                    syncWithLocation: true,
                    warnWhenUnsavedChanges: true,
                    projectId: 'resi-admin',
                  }}
                >
                  <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Public routes */}
                    <Route
                      path="/login"
                      element={
                        <PublicRoute>
                          <LoginPage />
                        </PublicRoute>
                      }
                    />

                    {/* Protected routes */}
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <DashboardPage />
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="/partners"
                      element={
                        <ProtectedRoute>
                          <PermissionGuard permission="manage_partners">
                            <PartnersListPage />
                          </PermissionGuard>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/partners/new"
                      element={
                        <ProtectedRoute>
                          <PermissionGuard permission="manage_partners">
                            <PartnerCreatePage />
                          </PermissionGuard>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/partners/onboarding"
                      element={
                        <ProtectedRoute>
                          <PermissionGuard permission="manage_partners">
                            <PartnerCreatePage />
                          </PermissionGuard>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/partners/:id"
                      element={
                        <ProtectedRoute>
                          <PermissionGuard permission="manage_partners">
                            <PartnerDetailPage />
                          </PermissionGuard>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="/partners/pending"
                      element={
                        <ProtectedRoute>
                          <PermissionGuard permission="manage_partners">
                            <div className="p-8">
                              <h2 className="text-2xl font-bold mb-4">Pending Partners</h2>
                              <p>Pending partner approval interface will be implemented here.</p>
                            </div>
                          </PermissionGuard>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="/bookings"
                      element={
                        <ProtectedRoute>
                          <BookingMonitorPage />
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* /users redirects to /users/customers */}
                    <Route
                      path="/users"
                      element={
                        <ProtectedRoute>
                          <Navigate to="/users/customers" replace />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/users/customers"
                      element={
                        <ProtectedRoute>
                          <PermissionGuard permission="manage_users">
                            <CustomerListPage />
                          </PermissionGuard>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="/users/admins"
                      element={
                        <ProtectedRoute>
                          <PermissionGuard permission="manage_users">
                            <AdminUsersListPage />
                          </PermissionGuard>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/staff"
                      element={
                        <ProtectedRoute>
                          <PermissionGuard permission="manage_staff">
                            <StaffListPage />
                          </PermissionGuard>
                        </ProtectedRoute>
                      }
                    />

                    {/* /support redirects to /support/tickets */}
                    <Route
                      path="/support"
                      element={
                        <ProtectedRoute>
                          <Navigate to="/support/tickets" replace />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/support/tickets"
                      element={
                        <ProtectedRoute>
                          <PermissionGuard permission="manage_tickets">
                            <div className="p-8">
                              <h2 className="text-2xl font-bold mb-4">Support Tickets</h2>
                              <p>Support ticket management interface will be implemented here.</p>
                            </div>
                          </PermissionGuard>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="/analytics"
                      element={
                        <ProtectedRoute>
                          <PermissionGuard permission="view_analytics">
                            <div className="p-8">
                              <h2 className="text-2xl font-bold mb-4">Analytics Dashboard</h2>
                              <p>Advanced analytics dashboard will be implemented here.</p>
                            </div>
                          </PermissionGuard>
                        </ProtectedRoute>
                      }
                    />
                    
                    <Route
                      path="/settings/audit"
                      element={
                        <ProtectedRoute>
                          <PermissionGuard permission="manage_system">
                            <AuditLogViewer />
                          </PermissionGuard>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/settings/*"
                      element={
                        <ProtectedRoute>
                          <PermissionGuard permission="manage_system">
                            <div className="p-8">
                              <h2 className="text-2xl font-bold mb-4">System Settings</h2>
                              <p>System settings and configuration will be implemented here.</p>
                            </div>
                          </PermissionGuard>
                        </ProtectedRoute>
                      }
                    />
                    
                    {/* Default redirect - will be handled by ProtectedRoute */}
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <Navigate to="/dashboard" replace />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="*"
                      element={
                        <ProtectedRoute>
                          <Navigate to="/dashboard" replace />
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                  </Suspense>

                  {/* <RefineKbar /> */}
                </Refine>
              </AuthProvider>
            {/* </RefineKbarProvider> */}
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;